import React, { useState } from 'react';
import { LegalModal } from './LegalModals';
import { StripeMockCheckoutModal, StripeCheckoutPlan } from './StripeMockCheckoutModal';
import { recordBaaApproval, triggerBaaPrint, getStoredBaaApprovals, BaaConsentRecord } from '../utils/baaConsentLogger';

export interface OrganizationMember {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'clinician' | 'specialist';
  specialistDomain?: string;
  status: 'active' | 'pending_approval';
  joinedAt: string;
  patientsCount: number;
}

export interface OrganizationState {
  id: string;
  name: string;
  domain: string;
  domainVerified: boolean;
  autoJoinEnabled: boolean;
  planTier: 'clinic_team' | 'enterprise';
  totalSeats: number;
  usedSeats: number;
  sponsoredPatientsLimit: number;
  activeSponsoredPatients: number;
  baaStatus: 'signed' | 'unsigned';
  baaSignedDate?: string;
  baaSignerName?: string;
}

export const OrganizationStudioView: React.FC = () => {
  const [org, setOrg] = useState<OrganizationState>({
    id: 'org_clinical_care',
    name: 'Clinical Care Center',
    domain: 'clinicalcare.org',
    domainVerified: true,
    autoJoinEnabled: true,
    planTier: 'clinic_team',
    totalSeats: 5,
    usedSeats: 4,
    sponsoredPatientsLimit: 30,
    activeSponsoredPatients: 18,
    baaStatus: 'signed',
    baaSignedDate: 'August 15, 2026',
    baaSignerName: 'Care Lead Practitioner, Ph.D. (Clinical Director)',
  });

  const [members, setMembers] = useState<OrganizationMember[]>([
    {
      id: 'mem_1',
      name: 'Care Lead Practitioner, Ph.D.',
      email: 'practitioner@clinicalcare.org',
      role: 'owner',
      status: 'active',
      joinedAt: 'Jan 10, 2026',
      patientsCount: 22,
    },
    {
      id: 'mem_2',
      name: 'Dr. Marcus Vance, M.D.',
      email: 'm.vance@clinicalcare.org',
      role: 'admin',
      specialistDomain: 'Substance & Alcohol Reduction',
      status: 'active',
      joinedAt: 'Feb 1, 2026',
      patientsCount: 14,
    },
    {
      id: 'mem_3',
      name: 'Care Specialist, MS, RD',
      email: 'specialist@clinicalcare.org',
      role: 'specialist',
      specialistDomain: 'Mindful Eating & Healthy Body Image',
      status: 'active',
      joinedAt: 'Mar 15, 2026',
      patientsCount: 12,
    },
    {
      id: 'mem_4',
      name: 'David Kim, LCSW',
      email: 'd.kim@clinicalcare.org',
      role: 'clinician',
      status: 'active',
      joinedAt: 'Apr 20, 2026',
      patientsCount: 0,
    },
    {
      id: 'mem_5',
      name: 'Dr. Elena Rostova, Psy.D.',
      email: 'e.rostova@clinicalcare.org',
      role: 'specialist',
      specialistDomain: 'Financial & Speculative Impulse',
      status: 'pending_approval',
      joinedAt: 'Today',
      patientsCount: 0,
    },
  ]);

  const [activeTab, setActiveTab] = useState<'roster' | 'domain' | 'baa' | 'billing' | 'audit'>('roster');
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [showBaaSignModal, setShowBaaSignModal] = useState<boolean>(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<StripeCheckoutPlan | null>(null);
  const [billingReceipts, setBillingReceipts] = useState<Array<{ id: string; planName: string; amount: string; date: string }>>([
    { id: 'ch_mock_982736', planName: 'Clinic Team Practice Suite (5 Seats)', amount: '$199.00', date: 'Aug 1, 2026' },
    { id: 'ch_mock_872615', planName: 'Clinic Team Practice Suite (5 Seats)', amount: '$199.00', date: 'Jul 1, 2026' },
  ]);
  const [newInviteEmail, setNewInviteEmail] = useState<string>('');
  const [newInviteName, setNewInviteName] = useState<string>('');
  const [newInviteRole, setNewInviteRole] = useState<'clinician' | 'specialist'>('clinician');
  const [newInviteDomain, setNewInviteDomain] = useState<string>('Mindful Eating & Healthy Body Image');

  const [baaSignerNameInput, setBaaSignerNameInput] = useState<string>('Care Lead Practitioner, Ph.D.');
  const [baaSignerTitleInput, setBaaSignerTitleInput] = useState<string>('Clinical Director');
  const [baaNpiInput, setBaaNpiInput] = useState<string>('1982736450');
  const [latestBaaRecord, setLatestBaaRecord] = useState<BaaConsentRecord | null>(() => {
    const stored = getStoredBaaApprovals();
    return stored.length > 0 ? stored[0] : null;
  });

  const handleApproveMember = (id: string) => {
    setMembers((prev) =>
      prev.map((m) => (m.id === id ? { ...m, status: 'active' } : m))
    );
  };

  const handleRemoveMember = (id: string) => {
    setMembers((prev) => prev.filter((m) => m.id !== id));
  };

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newInviteEmail || !newInviteName) return;

    const newMember: OrganizationMember = {
      id: `mem_${Date.now()}`,
      name: newInviteName,
      email: newInviteEmail,
      role: newInviteRole,
      specialistDomain: newInviteRole === 'specialist' ? newInviteDomain : undefined,
      status: 'active',
      joinedAt: 'Just now',
      patientsCount: 0,
    };

    setMembers((prev) => [...prev, newMember]);
    setShowInviteModal(false);
    setNewInviteEmail('');
    setNewInviteName('');
  };

  const handleSignBaa = async (e: React.FormEvent) => {
    e.preventDefault();
    const record = await recordBaaApproval({
      coveredEntityName: org.name,
      organizationId: org.id,
      npiOrTaxId: baaNpiInput,
      approverName: baaSignerNameInput,
      approverTitle: baaSignerTitleInput,
    });
    setLatestBaaRecord(record);
    setOrg((prev) => ({
      ...prev,
      baaStatus: 'signed',
      baaSignedDate: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      baaSignerName: `${baaSignerNameInput} (${baaSignerTitleInput})`,
    }));
    setShowBaaSignModal(false);
  };

  const handlePrintBaa = () => {
    if (latestBaaRecord) {
      triggerBaaPrint(latestBaaRecord);
    } else {
      const fallbackRecord: BaaConsentRecord = {
        agreementId: `baa_doc_${org.id}_active`,
        agreementVersion: 'v2.4-2026.09',
        coveredEntityName: org.name,
        organizationId: org.id,
        npiOrTaxId: baaNpiInput || '1982736450',
        approverName: baaSignerNameInput || 'Authorized Entity Representative',
        approverTitle: baaSignerTitleInput || 'Clinical Director',
        approverEmail: 'compliance@clinicalcare.org',
        approvedAtUtc: new Date().toISOString(),
        timestamp: Date.now(),
        ipAddress: '127.0.0.1 (Verified Session)',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Secure-Browser/1.0',
        approvalMethod: 'AFFIRMATIVE_DIGITAL_CLICKTHROUGH',
        statutoryRetention: '45_CFR_164_316_B_6_YEARS',
        cryptographicProofHash: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
        businessAssociateName: 'Luminous Break Cognitive Systems Inc.',
        businessAssociateSigner: 'Dr. Sarah Lin, MD, MS',
        businessAssociateTitle: 'Chief Medical & Compliance Officer',
      };
      triggerBaaPrint(fallbackRecord);
    }
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1200px', margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      {/* Organization Header Banner */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '20px',
        padding: '24px 32px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            backgroundColor: '#E8F5E9',
            color: '#1B4332',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
          }}>
            🏢
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#1B4332' }}>
                {org.name}
              </h1>
              <span style={{
                backgroundColor: '#D8F3DC',
                color: '#1B4332',
                fontSize: '11px',
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: '999px',
              }}>
                ✓ Verified Clinic Domain
              </span>
            </div>
            <span style={{ fontSize: '13px', color: '#64748B' }}>
              Domain: <strong>@{org.domain}</strong> • Plan: <strong>Clinic Team (5 Seats)</strong>
            </span>
          </div>
        </div>

        {/* Capacity & Usage Indicators */}
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <div style={{ textAlign: 'center', padding: '8px 16px', backgroundColor: '#FAF8F5', borderRadius: '12px', border: '1px solid #E6DFD5' }}>
            <div style={{ fontSize: '11px', color: '#64748B', fontWeight: 600 }}>Clinician Seats</div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#1B4332' }}>
              {members.filter(m => m.status === 'active').length} / {org.totalSeats}
            </div>
          </div>

          <div style={{ textAlign: 'center', padding: '8px 16px', backgroundColor: '#FAF8F5', borderRadius: '12px', border: '1px solid #E6DFD5' }}>
            <div style={{ fontSize: '11px', color: '#64748B', fontWeight: 600 }}>Sponsored Patients</div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#2D5A27' }}>
              {org.activeSponsoredPatients} / {org.sponsoredPatientsLimit}
            </div>
          </div>

          <button
            onClick={() => setShowInviteModal(true)}
            style={{
              backgroundColor: '#2D5A27',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 20px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(45,90,39,0.2)',
            }}
          >
            + Invite Clinician
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #E2E8F0', marginBottom: '24px' }}>
        {[
          { id: 'roster', label: '👥 Clinician & Specialist Roster' },
          { id: 'domain', label: '🔐 Verified Domain & Auto-Join' },
          { id: 'baa', label: '🛡️ HIPAA BAA Approval' },
          { id: 'billing', label: '💳 Subscription & Stripe Billing' },
          { id: 'audit', label: '📋 HIPAA § 164.312 Audit Logs' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #2D5A27' : '3px solid transparent',
              backgroundColor: 'transparent',
              color: activeTab === tab.id ? '#1B4332' : '#64748B',
              fontWeight: activeTab === tab.id ? 700 : 500,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB 1: CLINICIAN ROSTER */}
      {activeTab === 'roster' && (
        <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13.5px' }}>
            <thead>
              <tr style={{ backgroundColor: '#FAF8F5', borderBottom: '1px solid #E2E8F0', color: '#475569', fontWeight: 600 }}>
                <th style={{ padding: '14px 20px' }}>Practitioner Name</th>
                <th style={{ padding: '14px 20px' }}>Role & Permissions Scope</th>
                <th style={{ padding: '14px 20px' }}>Status</th>
                <th style={{ padding: '14px 20px' }}>Active Patients</th>
                <th style={{ padding: '14px 20px' }}>Joined Date</th>
                <th style={{ padding: '14px 20px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ fontWeight: 700, color: '#1E293B' }}>{member.name}</div>
                    <div style={{ fontSize: '12px', color: '#64748B' }}>{member.email}</div>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        backgroundColor: member.role === 'owner' ? '#FEF3C7' : member.role === 'specialist' ? '#E0F2FE' : '#E8F5E9',
                        color: member.role === 'owner' ? '#92400E' : member.role === 'specialist' ? '#0369A1' : '#166534',
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: '6px',
                        textTransform: 'uppercase',
                      }}>
                        {member.role}
                      </span>
                    </div>
                    {member.specialistDomain && (
                      <div style={{ fontSize: '12px', color: '#0369A1', marginTop: '4px', fontWeight: 500 }}>
                        Domain: {member.specialistDomain}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    {member.status === 'active' ? (
                      <span style={{ color: '#166534', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        ● Active Seat
                      </span>
                    ) : (
                      <span style={{ color: '#D97706', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        ⏳ Pending Approval
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '16px 20px', fontWeight: 600, color: '#334155' }}>
                    {member.patientsCount} patients
                  </td>
                  <td style={{ padding: '16px 20px', color: '#64748B', fontSize: '12.5px' }}>
                    {member.joinedAt}
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                    {member.status === 'pending_approval' ? (
                      <button
                        onClick={() => handleApproveMember(member.id)}
                        style={{
                          backgroundColor: '#2D5A27',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        ✓ Approve Seat
                      </button>
                    ) : member.role !== 'owner' ? (
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        style={{
                          backgroundColor: '#FEE2E2',
                          color: '#DC2626',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Remove
                      </button>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#94A3B8' }}>Clinic Owner</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 2: VERIFIED DOMAIN & AUTO-JOIN */}
      {activeTab === 'domain' && (
        <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '32px', border: '1px solid #E2E8F0' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1B4332', fontWeight: 800 }}>
            Institutional Email Domain Configuration
          </h3>
          <p style={{ color: '#64748B', fontSize: '14px', margin: '0 0 24px 0' }}>
            Clinicians who register with a verified email address ending in <strong>@{org.domain}</strong> can be automatically recognized and admitted to your clinic workspace.
          </p>

          <div style={{ backgroundColor: '#FAF8F5', borderRadius: '12px', padding: '20px', border: '1px solid #E6DFD5', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '15px', color: '#1E293B' }}>
                  Automatic Domain Auto-Join
                </div>
                <div style={{ fontSize: '13px', color: '#64748B', marginTop: '2px' }}>
                  When enabled, any staff member signing in with @{org.domain} is auto-joined to this clinic without manual invite tokens.
                </div>
              </div>

              <button
                onClick={() => setOrg(prev => ({ ...prev, autoJoinEnabled: !prev.autoJoinEnabled }))}
                style={{
                  backgroundColor: org.autoJoinEnabled ? '#2D5A27' : '#CBD5E1',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '999px',
                  padding: '8px 20px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                {org.autoJoinEnabled ? '✓ Enabled' : 'Disabled'}
              </button>
            </div>
          </div>

          <div style={{ backgroundColor: '#F8FAFC', borderRadius: '12px', padding: '20px', border: '1px solid #E2E8F0' }}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#334155', marginBottom: '8px' }}>
              Domain Verification DNS Record
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#FFFFFF', padding: '10px 14px', borderRadius: '8px', border: '1px solid #CBD5E1', color: '#0F172A' }}>
              TXT @ luminous-break-verification=lb_org_token_98fa7210b
            </div>
            <div style={{ fontSize: '12px', color: '#166534', marginTop: '8px', fontWeight: 600 }}>
              ✓ DNS TXT Record Verified by Cloudflare SSL on August 15, 2026.
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: HIPAA BAA APPROVAL */}
      {activeTab === 'baa' && (
        <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '32px', border: '1px solid #E2E8F0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#1B4332', fontWeight: 800 }}>
                HIPAA Business Associate Agreement (BAA)
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>
                Required documented arrangement under 45 CFR § 164.502(e) and § 164.504(e) for Covered Healthcare Entities.
              </p>
            </div>

            <span style={{
              backgroundColor: org.baaStatus === 'signed' ? '#D8F3DC' : '#FEF3C7',
              color: org.baaStatus === 'signed' ? '#166534' : '#92400E',
              fontWeight: 800,
              fontSize: '13px',
              padding: '6px 16px',
              borderRadius: '999px',
            }}>
              {org.baaStatus === 'signed' ? '✓ Approved & Enforced' : 'Action Required: Pending Approval'}
            </span>
          </div>

          <div style={{ backgroundColor: '#FAF8F5', borderRadius: '12px', padding: '20px', border: '1px solid #E6DFD5', marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Covered Entity Name</div>
                <div style={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>{org.name}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Authorized Entity Approver</div>
                <div style={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>{org.baaSignerName}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Approval Timestamp</div>
                <div style={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>{org.baaSignedDate}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Statutory Retention</div>
                <div style={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>6 Years (45 CFR § 164.316(b))</div>
              </div>
            </div>

            {latestBaaRecord && (
              <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #E2E8F0', fontSize: '12px' }}>
                <span style={{ color: '#64748B' }}>Cryptographic Proof Hash: </span>
                <code style={{ fontFamily: 'monospace', backgroundColor: '#FFFFFF', padding: '2px 6px', borderRadius: '4px', border: '1px solid #CBD5E1', color: '#0F172A' }}>
                  {latestBaaRecord.cryptographicProofHash}
                </code>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowBaaSignModal(true)}
              style={{
                backgroundColor: '#2D5A27',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              📄 Review / Re-Authorize BAA
            </button>
            <button
              onClick={handlePrintBaa}
              style={{
                backgroundColor: '#FFFFFF',
                color: '#2D5A27',
                border: '1.5px solid #2D5A27',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              🖨️ Print / Save as PDF
            </button>
          </div>
        </div>
      )}

      {/* TAB 4: HIPAA AUDIT LOGS */}
      {activeTab === 'audit' && (
        <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px', border: '1px solid #E2E8F0' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '17px', color: '#1B4332', fontWeight: 800 }}>
            HIPAA § 164.312(b) Technical Access & Modification Audit Trail
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { time: 'Today at 1:12 PM', actor: 'Care Lead Practitioner', event: 'Added Care Specialist, RD to Alex M. Care Team (Scope: Mindful Eating)', ip: '192.168.1.42 (Encrypted Session)' },
              { time: 'Today at 10:45 AM', actor: 'Dr. Marcus Vance', event: 'Updated Daytime/Nighttime strictness schedule for Jordan K.', ip: '10.0.0.18 (Hardware Key Authenticated)' },
              { time: 'Yesterday at 4:30 PM', actor: 'Care Lead Practitioner', event: 'Co-authored custom Urge Surfing CBT audio prompt for Alex M.', ip: '192.168.1.42 (Encrypted Session)' },
              { time: 'Aug 28, 2026', actor: 'David Kim, LCSW', event: 'Completed verified domain handshake (@clinicalcare.org)', ip: '172.16.0.5 (SSO Verified)' },
            ].map((log, idx) => (
              <div key={idx} style={{ padding: '12px 16px', backgroundColor: '#FAF8F5', borderRadius: '8px', border: '1px solid #E6DFD5', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B', fontSize: '11.5px', marginBottom: '4px' }}>
                  <span>{log.time} • <strong>{log.actor}</strong></span>
                  <span style={{ fontFamily: 'monospace' }}>{log.ip}</span>
                </div>
                <div style={{ color: '#1E293B', fontWeight: 600 }}>{log.event}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 4: STRIPE SUBSCRIPTION & BILLING */}
      {activeTab === 'billing' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Active Plan Overview Card */}
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px', border: '1px solid #E2E8F0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ backgroundColor: '#635BFF', color: '#FFFFFF', fontSize: '11px', padding: '3px 10px', borderRadius: '999px', fontWeight: 800 }}>
                    STRIPE TEST MODE ACTIVE
                  </span>
                  <span style={{ backgroundColor: '#E8F5E9', color: '#1B4332', fontSize: '11px', padding: '3px 10px', borderRadius: '999px', fontWeight: 700 }}>
                    ACTIVE SUBSCRIPTION
                  </span>
                </div>
                <h3 style={{ margin: '8px 0 2px', fontSize: '20px', fontWeight: 800, color: '#1B4332' }}>
                  Outpatient Clinic Starter Suite
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>
                  Includes 5 clinician seats, 30 active sponsored patient accounts, verified clinic domain, and signed HIPAA BAA.
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#1B4332' }}>$149.00</div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Billed monthly via Stripe (renews Sep 1, 2026)</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  setSelectedPlan({
                    id: 'clinic_team_annual',
                    name: 'Outpatient Clinic Starter (Annual Discount)',
                    price: '$1,430.00',
                    billingInterval: 'year',
                    description: '5 Clinician seats, 30 sponsored patients, dedicated onboarding (Save 20%).',
                  });
                  setShowCheckoutModal(true);
                }}
                style={{
                  backgroundColor: '#635BFF',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 18px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(99,91,255,0.25)',
                }}
              >
                ⚡ Switch to Annual ($1,430/yr - Save 20%)
              </button>

              <button
                onClick={() => {
                  setSelectedPlan({
                    id: 'addon_extra_clinician_seat',
                    name: 'Add-on: Extra Clinician Seat (+1 Seat)',
                    price: '$25.00',
                    billingInterval: 'month',
                    description: 'Adds 1 licensed therapist or clinical specialist seat to your clinic organization.',
                  });
                  setShowCheckoutModal(true);
                }}
                style={{
                  backgroundColor: '#FAF8F5',
                  color: '#1E293B',
                  border: '1px solid #CBD5E1',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                + Add Clinician Seat ($25/mo)
              </button>

              <button
                onClick={() => {
                  setSelectedPlan({
                    id: 'addon_extra_patients_pack',
                    name: 'Add-on: Extra Patient Pack (+5 Patients)',
                    price: '$20.00',
                    billingInterval: 'month',
                    description: 'Expands your clinic patient caseload capacity by 5 additional active patient seats.',
                  });
                  setShowCheckoutModal(true);
                }}
                style={{
                  backgroundColor: '#FAF8F5',
                  color: '#1E293B',
                  border: '1px solid #CBD5E1',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                + Add 5 Patients ($20/mo)
              </button>

              <button
                onClick={() => {
                  alert('Stripe Customer Portal (Mock Mode): In a live environment with your Stripe account connected, this redirects to the Stripe Customer Portal to update credit card on file, VAT details, and company tax ID.');
                }}
                style={{
                  backgroundColor: '#FAF8F5',
                  color: '#1E293B',
                  border: '1px solid #CBD5E1',
                  borderRadius: '8px',
                  padding: '10px 18px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                💳 Manage Payment Method
              </button>

              <button
                onClick={() => {
                  if (confirm('Cancel Subscription Confirmation:\n\nPursuant to our Terms of Service, you will retain full access through the end of your billing period (Sep 1, 2026). If you are within your first 30 days, you are also eligible for our 100% money-back guarantee by contacting billing@luminousbreak.com.\n\nProceed to cancel?')) {
                    alert('Subscription cancelled in mock mode. Access retained through Sep 1, 2026.');
                  }
                }}
                style={{
                  backgroundColor: 'transparent',
                  color: '#EF4444',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel Subscription
              </button>
            </div>
          </div>

          {/* Simulated Invoices Table */}
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '24px', border: '1px solid #E2E8F0' }}>
            <h4 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: 800, color: '#1B4332' }}>
              🧾 Simulated Billing History & Receipts
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#F8FAFC', textAlign: 'left', borderBottom: '1px solid #E2E8F0' }}>
                  <th style={{ padding: '10px 14px' }}>Invoice ID</th>
                  <th style={{ padding: '10px 14px' }}>Plan Description</th>
                  <th style={{ padding: '10px 14px' }}>Date</th>
                  <th style={{ padding: '10px 14px' }}>Amount</th>
                  <th style={{ padding: '10px 14px' }}>Status</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right' }}>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {billingReceipts.map((rcpt, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontWeight: 600, color: '#635BFF' }}>{rcpt.id}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1E293B' }}>{rcpt.planName}</td>
                    <td style={{ padding: '12px 14px', color: '#64748B' }}>{rcpt.date}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: '#1B4332' }}>{rcpt.amount}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ backgroundColor: '#E8F5E9', color: '#166534', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700 }}>
                        PAID
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                      <button
                        onClick={() => alert(`Simulated Stripe PDF Receipt for ${rcpt.id} (${rcpt.amount}) downloaded!`)}
                        style={{ background: 'none', border: 'none', color: '#2D5A27', fontWeight: 600, cursor: 'pointer', fontSize: '12px' }}
                      >
                        📄 Download PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* INVITE CLINICIAN MODAL */}
      <LegalModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite Practitioner to Clinic Team"
      >
        <form onSubmit={handleInviteSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>Full Name & Credentials</label>
            <input
              type="text"
              required
              placeholder="e.g. Dr. Emily Thorne, Psy.D."
              value={newInviteName}
              onChange={(e) => setNewInviteName(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>Clinic Email Address (@clinicalcare.org)</label>
            <input
              type="email"
              required
              placeholder="e.thorne@clinicalcare.org"
              value={newInviteEmail}
              onChange={(e) => setNewInviteEmail(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>Role Type</label>
            <select
              value={newInviteRole}
              onChange={(e) => setNewInviteRole(e.target.value as any)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', backgroundColor: '#FFFFFF', boxSizing: 'border-box' }}
            >
              <option value="clinician">General Clinician (Full Patient Access)</option>
              <option value="specialist">Domain Specialist (Restricted Scope)</option>
            </select>
          </div>

          {newInviteRole === 'specialist' && (
            <div>
              <label style={{ fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>Assigned Specialist Domain</label>
              <select
                value={newInviteDomain}
                onChange={(e) => setNewInviteDomain(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', backgroundColor: '#FFFFFF', boxSizing: 'border-box' }}
              >
                <option value="Mindful Eating & Healthy Body Image">Mindful Eating & Healthy Body Image (Dietitian / RD)</option>
                <option value="Financial & Speculative Impulse">Financial & Speculative Impulse (Financial Coach)</option>
                <option value="Substance & Alcohol Reduction">Substance & Alcohol Reduction (Addiction Specialist)</option>
                <option value="Mindful Media & Doomscrolling">Mindful Media & Doomscrolling (Behavioral Specialist)</option>
              </select>
            </div>
          )}

          <button
            type="submit"
            style={{
              backgroundColor: '#2D5A27',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
              marginTop: '8px',
            }}
          >
            Send Institutional Invitation ➔
          </button>
        </form>
      </LegalModal>

      {/* DIGITAL HIPAA BAA APPROVAL MODAL */}
      <LegalModal
        isOpen={showBaaSignModal}
        onClose={() => setShowBaaSignModal(false)}
        title="Review & Approve HIPAA Business Associate Agreement"
      >
        <form onSubmit={handleSignBaa} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <p style={{ fontSize: '13px', color: '#475569', margin: 0, lineHeight: '1.5' }}>
            This Agreement governs the technical safeguards and confidential processing of electronic Protected Health Information (ePHI) in compliance with the HIPAA Privacy Rule and Security Rule (45 CFR Parts 160 and 164).
          </p>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Authorized Entity Approver Full Name</label>
            <input
              type="text"
              required
              value={baaSignerNameInput}
              onChange={(e) => setBaaSignerNameInput(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #CBD5E1', boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Approver Professional Title (e.g., Clinical Director, MD, LCSW)</label>
            <input
              type="text"
              required
              value={baaSignerTitleInput}
              onChange={(e) => setBaaSignerTitleInput(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #CBD5E1', boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Clinic Organization NPI or Tax ID (EIN)</label>
            <input
              type="text"
              required
              value={baaNpiInput}
              onChange={(e) => setBaaNpiInput(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #CBD5E1', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ backgroundColor: '#E8F5E9', padding: '12px', borderRadius: '8px', fontSize: '12px', color: '#166534', lineHeight: '1.5' }}>
            ✓ By approving below, you affirm on behalf of <strong>{org.name}</strong> that you have reviewed and approve this Business Associate Agreement pursuant to 45 CFR § 164.502(e) and § 164.504(e). Electronic approval constitutes an effective documented arrangement under federal law with mandatory 6-year statutory retention (45 CFR § 164.316(b)).
          </div>

          <button
            type="submit"
            style={{
              backgroundColor: '#2D5A27',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Authorize & Approve HIPAA BAA ➔
          </button>
        </form>
      </LegalModal>

      {/* STRIPE MOCK CHECKOUT MODAL */}
      <StripeMockCheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        plan={selectedPlan}
        onSuccess={(receipt) => {
          setBillingReceipts((prev) => [receipt, ...prev]);
          alert(`✓ Stripe Mock Checkout Succeeded!\n\nPlan: ${receipt.planName}\nAmount Charged: ${receipt.amount}\nSimulated Invoice ID: ${receipt.id}\nReceipt emailed to billing@clinicalcare.org`);
        }}
      />
    </div>
  );
};

export default OrganizationStudioView;
